"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ReadableEthersLiquity = void 0;
const lib_base_1 = require("@liquity/lib-base");
const _utils_1 = require("./_utils");
const EthersLiquityConnection_1 = require("./EthersLiquityConnection");
const BlockPolledLiquityStore_1 = require("./BlockPolledLiquityStore");
// TODO: these are constant in the contracts, so it doesn't make sense to make a call for them,
// but to avoid having to update them here when we change them in the contracts, we could read
// them once after deployment and save them to LiquityDeployment.
const MINUTE_DECAY_FACTOR = lib_base_1.Decimal.from("0.999037758833783000");
const BETA = lib_base_1.Decimal.from(2);
var BackendTroveStatus;
(function (BackendTroveStatus) {
    BackendTroveStatus[BackendTroveStatus["nonExistent"] = 0] = "nonExistent";
    BackendTroveStatus[BackendTroveStatus["active"] = 1] = "active";
    BackendTroveStatus[BackendTroveStatus["closedByOwner"] = 2] = "closedByOwner";
    BackendTroveStatus[BackendTroveStatus["closedByLiquidation"] = 3] = "closedByLiquidation";
    BackendTroveStatus[BackendTroveStatus["closedByRedemption"] = 4] = "closedByRedemption";
})(BackendTroveStatus || (BackendTroveStatus = {}));
const userTroveStatusFrom = (backendStatus) => backendStatus === BackendTroveStatus.nonExistent
    ? "nonExistent"
    : backendStatus === BackendTroveStatus.active
        ? "open"
        : backendStatus === BackendTroveStatus.closedByOwner
            ? "closedByOwner"
            : backendStatus === BackendTroveStatus.closedByLiquidation
                ? "closedByLiquidation"
                : backendStatus === BackendTroveStatus.closedByRedemption
                    ? "closedByRedemption"
                    : _utils_1.panic(new Error(`invalid backendStatus ${backendStatus}`));
const convertToDate = (timestamp) => new Date(timestamp * 1000);
const validSortingOptions = ["ascendingCollateralRatio", "descendingCollateralRatio"];
const expectPositiveInt = (obj, key) => {
    if (obj[key] !== undefined) {
        if (!Number.isInteger(obj[key])) {
            throw new Error(`${key} must be an integer`);
        }
        if (obj[key] < 0) {
            throw new Error(`${key} must not be negative`);
        }
    }
};
/**
 * Ethers-based implementation of {@link @liquity/lib-base#ReadableLiquity}.
 *
 * @public
 */
class ReadableEthersLiquity {
    /** @internal */
    constructor(connection) {
        this.connection = connection;
    }
    /** @internal */
    static _from(connection) {
        const readable = new ReadableEthersLiquity(connection);
        return connection.useStore === "blockPolled"
            ? new _BlockPolledReadableEthersLiquity(readable)
            : readable;
    }
    /**
     * Connect to the Liquity protocol and create a `ReadableEthersLiquity` object.
     *
     * @param signerOrProvider - Ethers `Signer` or `Provider` to use for connecting to the Ethereum
     *                           network.
     * @param optionalParams - Optional parameters that can be used to customize the connection.
     */
    static async connect(signerOrProvider, optionalParams) {
        return ReadableEthersLiquity._from(await EthersLiquityConnection_1._connect(signerOrProvider, optionalParams));
    }
    hasStore() {
        return false;
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getTotalRedistributed} */
    async getTotalRedistributed(assetAddress, overrides) {
        const { troveManagerHelpers } = EthersLiquityConnection_1._getContracts(this.connection);
        const [collateral, debt] = await Promise.all([
            troveManagerHelpers.L_ASSETS(assetAddress, { ...overrides }).then(_utils_1.decimalify),
            troveManagerHelpers.L_SLSDDebts(assetAddress, { ...overrides }).then(_utils_1.decimalify)
        ]);
        return new lib_base_1.Trove(collateral, debt);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getTroveBeforeRedistribution} */
    async getTroveBeforeRedistribution(assetAddress, address, overrides) {
        address !== null && address !== void 0 ? address : (address = EthersLiquityConnection_1._requireAddress(this.connection));
        const { troveManagerHelpers } = EthersLiquityConnection_1._getContracts(this.connection);
        const [trove, snapshot] = await Promise.all([
            troveManagerHelpers.Troves(address, assetAddress, { ...overrides }),
            troveManagerHelpers.getRewardSnapshots(assetAddress, address, { ...overrides })
        ]);
        if (trove.status === BackendTroveStatus.active) {
            return new lib_base_1.TroveWithPendingRedistribution(address, userTroveStatusFrom(trove.status), _utils_1.decimalify(trove.coll), _utils_1.decimalify(trove.debt), _utils_1.decimalify(trove.stake), new lib_base_1.Trove(_utils_1.decimalify(snapshot.asset), _utils_1.decimalify(snapshot.SLSDDebt)));
        }
        else {
            return new lib_base_1.TroveWithPendingRedistribution(address, userTroveStatusFrom(trove.status));
        }
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getTrove} */
    async getTrove(assetAddress, address, overrides) {
        const [trove, totalRedistributed] = await Promise.all([
            this.getTroveBeforeRedistribution(assetAddress, address, overrides),
            this.getTotalRedistributed(assetAddress, overrides)
        ]);
        return trove.applyRedistribution(totalRedistributed);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getNumberOfTroves} */
    async getNumberOfTroves(assetAddress, overrides) {
        const { troveManagerHelpers } = EthersLiquityConnection_1._getContracts(this.connection);
        return (await troveManagerHelpers.getTroveOwnersCount(assetAddress, { ...overrides })).toNumber();
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getPrice} */
    getPrice(assetAddress, overrides) {
        const { priceFeed } = EthersLiquityConnection_1._getContracts(this.connection);
        return priceFeed.callStatic.fetchPrice(assetAddress, { ...overrides }).then(_utils_1.decimalify);
    }
    /** @internal */
    async _getActivePool(assetAddress, overrides) {
        const { activePool } = EthersLiquityConnection_1._getContracts(this.connection);
        const [activeCollateral, activeDebt] = await Promise.all([
            activePool.getAssetBalance(assetAddress, { ...overrides }),
            activePool.getSLSDDebt(assetAddress, { ...overrides })
        ].map(getBigNumber => getBigNumber.then(_utils_1.decimalify)));
        return new lib_base_1.Trove(activeCollateral, activeDebt);
    }
    /** @internal */
    async _getDefaultPool(assetAddress, overrides) {
        const { defaultPool } = EthersLiquityConnection_1._getContracts(this.connection);
        const [liquidatedCollateral, closedDebt] = await Promise.all([
            defaultPool.getAssetBalance(assetAddress, { ...overrides }),
            defaultPool.getSLSDDebt(assetAddress, { ...overrides })
        ].map(getBigNumber => getBigNumber.then(_utils_1.decimalify)));
        return new lib_base_1.Trove(liquidatedCollateral, closedDebt);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getTotal} */
    async getTotal(assetAddress, overrides) {
        const [activePool, defaultPool] = await Promise.all([
            this._getActivePool(assetAddress, overrides),
            this._getDefaultPool(assetAddress, overrides)
        ]);
        return activePool.add(defaultPool);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getStabilityDeposit} */
    async getStabilityDeposit(spAddress, address, overrides) {
        address !== null && address !== void 0 ? address : (address = EthersLiquityConnection_1._requireAddress(this.connection));
        const stabilityPool = EthersLiquityConnection_1._getStabilityPoolContractByAddress(spAddress, this.connection);
        if (!stabilityPool) {
            return new lib_base_1.StabilityDeposit(lib_base_1.Decimal.ZERO, lib_base_1.Decimal.ZERO, lib_base_1.Decimal.ZERO, lib_base_1.Decimal.ZERO);
        }
        const [initialValue, currentSLSD, collateralGain, psyReward] = await Promise.all([
            stabilityPool.deposits(address, { ...overrides }),
            stabilityPool.getCompoundedSLSDDeposit(address, { ...overrides }),
            stabilityPool.getDepositorAssetGain(address, { ...overrides }),
            stabilityPool.getDepositorPSYGain(address, { ...overrides })
        ]);
        return new lib_base_1.StabilityDeposit(_utils_1.decimalify(initialValue), _utils_1.decimalify(currentSLSD), _utils_1.decimalify(collateralGain), _utils_1.decimalify(psyReward));
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getSLSDInStabilityPool} */
    getSLSDInStabilityPool(spAddress, overrides) {
        const stabilityPool = EthersLiquityConnection_1._getStabilityPoolContractByAddress(spAddress, this.connection);
        if (!stabilityPool) {
            return new Promise(resolve => resolve(lib_base_1.Decimal.ZERO));
        }
        return stabilityPool.getTotalSLSDDeposits({ ...overrides }).then(_utils_1.decimalify);
    }
    /**  */
    getAssetBalance(assetAddress, address, overrides) {
        address !== null && address !== void 0 ? address : (address = EthersLiquityConnection_1._requireAddress(this.connection));
        const assetToken = EthersLiquityConnection_1._getERC20ContractByAddress(assetAddress, this.connection);
        if (!assetToken) {
            return new Promise(resolve => resolve(lib_base_1.Decimal.ZERO));
        }
        return assetToken.balanceOf(address, { ...overrides }).then(_utils_1.decimalify);
    }
    /**  */
    getAssetAllowance(assetAddress, address, overrides) {
        address !== null && address !== void 0 ? address : (address = EthersLiquityConnection_1._requireAddress(this.connection));
        const assetToken = EthersLiquityConnection_1._getERC20ContractByAddress(assetAddress, this.connection);
        if (!assetToken) {
            return new Promise(resolve => resolve(lib_base_1.Decimal.ZERO));
        }
        const { borrowerOperations: borrowerOperationsAddress } = this.connection.addresses;
        return assetToken.allowance(address, borrowerOperationsAddress, { ...overrides }).then(_utils_1.decimalify);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getSLSDBalance} */
    getSLSDBalance(address, overrides) {
        address !== null && address !== void 0 ? address : (address = EthersLiquityConnection_1._requireAddress(this.connection));
        const { slsdToken } = EthersLiquityConnection_1._getContracts(this.connection);
        return slsdToken.balanceOf(address, { ...overrides }).then(_utils_1.decimalify);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getCollateralSurplusBalance} */
    getCollateralSurplusBalance(assetAddress, address, overrides) {
        address !== null && address !== void 0 ? address : (address = EthersLiquityConnection_1._requireAddress(this.connection));
        const { collSurplusPool } = EthersLiquityConnection_1._getContracts(this.connection);
        return collSurplusPool.getCollateral(assetAddress, address, { ...overrides }).then(_utils_1.decimalify);
    }
    async getTroves(assetAddress, params, overrides) {
        var _a, _b;
        const { multiTroveGetter } = EthersLiquityConnection_1._getContracts(this.connection);
        expectPositiveInt(params, "first");
        expectPositiveInt(params, "startingAt");
        if (!validSortingOptions.includes(params.sortedBy)) {
            throw new Error(`sortedBy must be one of: ${validSortingOptions.map(x => `"${x}"`).join(", ")}`);
        }
        const [totalRedistributed, backendTroves] = await Promise.all([
            // PSY- multi collateral: objectを返して合致するcoolateralの分を取り出す
            params.beforeRedistribution ? undefined : this.getTotalRedistributed(assetAddress, { ...overrides }),
            multiTroveGetter.getMultipleSortedTroves(assetAddress, params.sortedBy === "descendingCollateralRatio"
                ? (_a = params.startingAt) !== null && _a !== void 0 ? _a : 0 : -(((_b = params.startingAt) !== null && _b !== void 0 ? _b : 0) + 1), params.first, { ...overrides })
        ]);
        const troves = mapBackendTroves(backendTroves);
        if (totalRedistributed) {
            return troves.map(trove => trove.applyRedistribution(totalRedistributed));
        }
        else {
            return troves;
        }
    }
    /** @internal */
    _getBlockTimestamp(blockTag) {
        return EthersLiquityConnection_1._getBlockTimestamp(this.connection, blockTag);
    }
    /** @internal */
    async _getFeesFactory(assetAddress, overrides) {
        const { troveManagerHelpers } = EthersLiquityConnection_1._getContracts(this.connection);
        const [lastFeeOperationTime, baseRateWithoutDecay] = await Promise.all([
            troveManagerHelpers.lastFeeOperationTime(assetAddress, { ...overrides }),
            troveManagerHelpers.baseRate(assetAddress, { ...overrides }).then(_utils_1.decimalify)
        ]);
        return (blockTimestamp, recoveryMode) => new lib_base_1.Fees(baseRateWithoutDecay, MINUTE_DECAY_FACTOR, BETA, convertToDate(lastFeeOperationTime.toNumber()), convertToDate(blockTimestamp), recoveryMode);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getFees} */
    async getFees(assetAddress, overrides) {
        const [createFees, total, price, blockTimestamp] = await Promise.all([
            this._getFeesFactory(assetAddress, overrides),
            this.getTotal(assetAddress, overrides),
            this.getPrice(assetAddress, overrides),
            this._getBlockTimestamp(overrides === null || overrides === void 0 ? void 0 : overrides.blockTag)
        ]);
        return createFees(blockTimestamp, total.collateralRatioIsBelowCritical(price));
    }
    async getIsMinted(assetAddress, address, overrides) {
        // FIXME single Collateral mock
        if (!this.connection._assetTokenIsMock) {
            return undefined;
        }
        address !== null && address !== void 0 ? address : (address = EthersLiquityConnection_1._requireAddress(this.connection));
        const assetContract = EthersLiquityConnection_1._getERC20ContractByAddress(assetAddress, this.connection);
        const mockToken = assetContract;
        return mockToken === null || mockToken === void 0 ? void 0 : mockToken.minted(address, { ...overrides });
    }
}
exports.ReadableEthersLiquity = ReadableEthersLiquity;
const mapBackendTroves = (troves) => troves.map(trove => new lib_base_1.TroveWithPendingRedistribution(trove.owner, "open", // These Troves are coming from the SortedTroves list, so they must be open
_utils_1.decimalify(trove.coll), _utils_1.decimalify(trove.debt), _utils_1.decimalify(trove.stake), new lib_base_1.Trove(_utils_1.decimalify(trove.snapshotAsset), _utils_1.decimalify(trove.snapshotSLSDDebt))));
class _BlockPolledReadableEthersLiquity {
    constructor(readable) {
        const store = new BlockPolledLiquityStore_1.BlockPolledLiquityStore(readable);
        this.store = store;
        this.connection = readable.connection;
        this._readable = readable;
    }
    _blockHit(overrides) {
        return (!overrides ||
            overrides.blockTag === undefined ||
            overrides.blockTag === this.store.state.blockTag);
    }
    _userHit(address, overrides) {
        return (this._blockHit(overrides) &&
            (address === undefined || address === this.store.connection.userAddress));
    }
    _frontendHit(address, overrides) {
        return (this._blockHit(overrides) &&
            (address === undefined || address === this.store.connection.frontendTag));
    }
    hasStore(store) {
        return store === undefined || store === "blockPolled";
    }
    async getTotalRedistributed(assetAddress, overrides) {
        const collateralKey = EthersLiquityConnection_1.getCollateralKey(assetAddress, this.connection);
        return this._blockHit(overrides) && collateralKey
            ? this.store.state.collateralStates[collateralKey].totalRedistributed
            : this._readable.getTotalRedistributed(assetAddress, overrides);
    }
    async getTroveBeforeRedistribution(assetAddress, address, overrides) {
        const collateralKey = EthersLiquityConnection_1.getCollateralKey(assetAddress, this.connection);
        return this._userHit(address, overrides) && collateralKey
            ? this.store.state.collateralStates[collateralKey].troveBeforeRedistribution
            : this._readable.getTroveBeforeRedistribution(assetAddress, address, overrides);
    }
    async getTrove(assetAddress, address, overrides) {
        const collateralKey = EthersLiquityConnection_1.getCollateralKey(assetAddress, this.connection);
        return this._userHit(address, overrides) && collateralKey
            ? this.store.state.collateralDerivedStates[collateralKey].trove
            : this._readable.getTrove(assetAddress, address, overrides);
    }
    async getNumberOfTroves(assetAddress, overrides) {
        const collateralKey = EthersLiquityConnection_1.getCollateralKey(assetAddress, this.connection);
        return this._blockHit(overrides) && collateralKey
            ? this.store.state.collateralStates[collateralKey].numberOfTroves
            : this._readable.getNumberOfTroves(assetAddress, overrides);
    }
    async getPrice(assetAddress, overrides) {
        const collateralKey = EthersLiquityConnection_1.getCollateralKey(assetAddress, this.connection);
        return this._blockHit(overrides) && collateralKey
            ? this.store.state.collateralStates[collateralKey].price
            : this._readable.getPrice(assetAddress, overrides);
    }
    async getTotal(assetAddress, overrides) {
        const collateralKey = EthersLiquityConnection_1.getCollateralKey(assetAddress, this.connection);
        return this._blockHit(overrides) && collateralKey
            ? this.store.state.collateralStates[collateralKey].total
            : this._readable.getTotal(assetAddress, overrides);
    }
    async getStabilityDeposit(spAddress, address, overrides) {
        const collateralKey = EthersLiquityConnection_1.getCollateralKey(spAddress, this.connection);
        return this._userHit(address, overrides) && collateralKey
            ? this.store.state.collateralStates[collateralKey].stabilityDeposit
            : this._readable.getStabilityDeposit(spAddress, address, overrides);
    }
    async getSLSDInStabilityPool(spAddress, overrides) {
        const collateralKey = EthersLiquityConnection_1.getCollateralKey(spAddress, this.connection);
        return this._blockHit(overrides) && collateralKey
            ? this.store.state.collateralStates[collateralKey].slsdInStabilityPool
            : this._readable.getSLSDInStabilityPool(spAddress, overrides);
    }
    async getAssetBalance(assetAddress, address, overrides) {
        const collateralKey = EthersLiquityConnection_1.getCollateralKey(assetAddress, this.connection);
        return this._userHit(address, overrides) && collateralKey
            ? this.store.state.collateralStates[collateralKey].balance
            : this._readable.getAssetBalance(assetAddress, address, overrides);
    }
    async getAssetAllowance(assetAddress, address, overrides) {
        const collateralKey = EthersLiquityConnection_1.getCollateralKey(assetAddress, this.connection);
        return this._userHit(address, overrides) && collateralKey
            // TODO-PSY: state
            ? this.store.state.collateralStates[collateralKey].allowance
            : this._readable.getAssetAllowance(assetAddress, address, overrides);
    }
    async getSLSDBalance(address, overrides) {
        return this._userHit(address, overrides)
            ? this.store.state.slsdBalance
            : this._readable.getSLSDBalance(address, overrides);
    }
    async getCollateralSurplusBalance(assetAddress, address, overrides) {
        const collateralKey = EthersLiquityConnection_1.getCollateralKey(assetAddress, this.connection);
        return this._userHit(address, overrides) && collateralKey
            ? this.store.state.collateralStates[collateralKey].collateralSurplusBalance
            : this._readable.getCollateralSurplusBalance(assetAddress, address, overrides);
    }
    async _getBlockTimestamp(blockTag) {
        return this._blockHit({ blockTag })
            ? this.store.state.blockTimestamp
            : this._readable._getBlockTimestamp(blockTag);
    }
    async _getFeesFactory(assetAddress, overrides) {
        const collateralKey = EthersLiquityConnection_1.getCollateralKey(assetAddress, this.connection);
        return this._blockHit(overrides) && collateralKey
            ? this.store.state._feesFactory[collateralKey]
            : this._readable._getFeesFactory(assetAddress, overrides);
    }
    async getFees(assetAddress, overrides) {
        const collateralKey = EthersLiquityConnection_1.getCollateralKey(assetAddress, this.connection);
        return this._blockHit(overrides) && collateralKey
            ? this.store.state.collateralDerivedStates[collateralKey].fees
            : this._readable.getFees(assetAddress, overrides);
    }
    async getIsMinted(assetAddress, address, overrides) {
        const collateralKey = EthersLiquityConnection_1.getCollateralKey(assetAddress, this.connection);
        return this._userHit(address, overrides) && collateralKey
            ? this.store.state.collateralStates[collateralKey].isMinted
            : this._readable.getIsMinted(assetAddress, address, overrides);
    }
    getTroves(assetAddress, params, overrides) {
        return this._readable.getTroves(assetAddress, params, overrides);
    }
    _getActivePool() {
        throw new Error("Method not implemented.");
    }
    _getDefaultPool() {
        throw new Error("Method not implemented.");
    }
    _getRemainingLiquidityMiningPSYRewardCalculator() {
        throw new Error("Method not implemented.");
    }
}
//# sourceMappingURL=ReadableEthersLiquity.js.map